import { getErrorMessage, getErrorReason } from '@ms/yammer-telemetry';
import { flushSync } from 'react-dom';

import type { PromiseThunkAction } from '../types';

import cachedApiAction from './cachedApiAction';
import { endManagedApiMeasurement, getApiMeasureName, startManagedApiMeasurement } from './performance';
import type { ManagedApiActionOptions } from './types';

interface ManagedApiActionResult {
  readonly cacheHit: boolean | undefined;
}

const managedApiAction =
  <TResponse>(options: ManagedApiActionOptions<TResponse>): PromiseThunkAction<ManagedApiActionResult> =>
  async (dispatch) => {
    try {
      const pendingAction = options.lifecycle.pending();

      // Temporary workaround to support React 18. React 18 batches state updates inside promises, so areas of our existing
      // code that rely on sequential state updates, like with useDidPublishActionJustComplete.ts break. The
      // `flushSyncLifecycleActions` option was added to selectively disable batching for these specific cases
      // React 18 change: https://react.dev/blog/2022/03/08/react-18-upgrade-guide#automatic-batching
      // Tech debt ticket: https://dev.azure.com/yammer/engineering/_workitems/edit/382827
      if (options.flushSyncLifecycleActions) {
        flushSync(() => {
          dispatch(pendingAction);
        });
      } else {
        dispatch(pendingAction);
      }

      const coreAction = cachedApiAction<TResponse>(options);
      const { response, cacheHit } = await dispatch(coreAction);
      const fulfilledAction = options.lifecycle.fulfilled(response);

      if (options.flushSyncLifecycleActions) {
        flushSync(() => {
          dispatch(fulfilledAction);
        });
      } else {
        dispatch(fulfilledAction);
      }

      return { cacheHit };
    } catch (err) {
      const rejectedAction = options.lifecycle.rejected(err);
      dispatch(rejectedAction);
      throw err;
    }
  };

type PerformanceReportedManagedApiAction = <TResponse>(
  options: ManagedApiActionOptions<TResponse>
) => PromiseThunkAction<void>;

const performanceReportedManagedApiAction: PerformanceReportedManagedApiAction = (options) => async (dispatch) => {
  const apiMeasureName = getApiMeasureName(options.request);
  const measure = startManagedApiMeasurement(apiMeasureName);
  let requestRejected = false;
  let error: unknown;
  let cacheHit;

  try {
    ({ cacheHit } = await dispatch(managedApiAction(options)));
  } catch (caughtError) {
    requestRejected = true;
    error = caughtError;
    throw caughtError;
  } finally {
    endManagedApiMeasurement({
      ...measure,
      eventProperties: {
        ...measure.eventProperties,
        apiName: apiMeasureName,
        actionName: options.telemetry?.actionName,
        actionType: options.telemetry?.actionType,
        cacheExists: options.telemetry?.cacheExists,
        cachingStrategy: options.caching?.strategy,
        endedWithError: requestRejected,
        message: getErrorMessage(error),
        reason: getErrorReason(error),
        idempotencyToken: options.telemetry?.idempotencyToken,
        requestId: '',
        initiator: options.request.initiator,
        cacheHit,
        ...options.telemetry?.customProperties,
      },
    });
  }
};

export default performanceReportedManagedApiAction;
